import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowDown, KeyboardArrowUp, Language } from '@mui/icons-material';
import { Button, CircularProgress, Menu, MenuItem } from '@mui/material';
import { Flow } from '@/constants/flow';
import { LANG_SWITCH_OPTIONS } from '@/constants/locale';
import { useFlow } from '@/hooks/use-flow';
import { t } from 'i18next';

export const SwitchLanguage: React.FC = () => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setFlow } = useFlow();
  const open = Boolean(anchorEl);

  const current = LANG_SWITCH_OPTIONS.find((item) => item.value === i18n.language)?.label || 'English';

  const handleChangeLanguage = async (value: string) => {
    if (i18n.language === value || isLoading) return;

    const prevLang = i18n.language;
    setAnchorEl(null);
    setIsLoading(true);

    try {
      await i18n.changeLanguage(value);
      if (!i18n.hasResourceBundle(value, 'translation')) {
        await i18n.changeLanguage(prevLang);
        setFlow(Flow.EXCEPTION_NETWORK_ERROR);
      }
    } catch {
      await i18n.changeLanguage(prevLang);
      setFlow(Flow.EXCEPTION_NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Button
        color="inherit"
        id="basic-button"
        size="small"
        sx={(theme) => ({
          fontWeight: 400,
          '.MuiButton-startIcon': {
            marginLeft: theme.spacing(0.5),
            marginRight: theme.spacing(0.5),
          },
          '.MuiButton-endIcon': {
            marginLeft: theme.spacing(0.5),
            marginRight: theme.spacing(0.5),
          },
        })}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={isLoading ? <CircularProgress color="inherit" size={16} /> : <Language />}
        endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        aria-label={t('select_language_label', { lang: current })}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={isLoading}
      >
        {current}
      </Button>

      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: { style: { maxHeight: 300 } },
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        {LANG_SWITCH_OPTIONS.map((item) => {
          return (
            <MenuItem
              key={item.value}
              selected={item.value === i18n.language}
              onClick={() => handleChangeLanguage(item.value)}
              value={item.value}
            >
              {item.label}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};
